import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ForceGraph3D from 'react-force-graph-3d'
import { Box } from '@chakra-ui/react'
import KnowledgeGraphPanel from './KnowledgeGraphPanel'
import type { GraphData, GraphNode, GraphLink } from './hooks/useKnowledgeGraph'
import * as THREE from 'three'

interface KnowledgeGraph3DProps {
  graphData: GraphData
  width: number
  height: number
}

interface ForceGraph3DHandle {
  zoomToFit: (duration?: number, padding?: number) => void
  cameraPosition: (
    position?: { x: number; y: number; z: number },
    lookAt?: { x: number; y: number; z: number },
    duration?: number,
  ) => void
}

const HIGHLIGHT_COLOR = '#f59e0b'

const NODE_COLORS: Record<string, number> = {
  '#1e3a5f': 0x1e3a5f,
  '#2563eb': 0x2563eb,
  '#0d9488': 0x0d9488,
  '#16a34a': 0x16a34a,
  '#ea580c': 0xea580c,
  '#a855f7': 0xa855f7,
}

export default function KnowledgeGraph3D({ graphData, width, height }: KnowledgeGraph3DProps) {
  const graphRef = useRef<ForceGraph3DHandle>(null)
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null)
  const hasZoomedRef = useRef(false)
  const [layoutFrozen, setLayoutFrozen] = useState(false)

  const handleEngineStop = useCallback(() => {
    if (layoutFrozen) return
    for (const node of graphData.nodes) {
      const n = node as any
      if (n.x != null) n.fx = n.x
      if (n.y != null) n.fy = n.y
      if (n.z != null) n.fz = n.z
    }
    setLayoutFrozen(true)
  }, [layoutFrozen, graphData.nodes])

  useEffect(() => {
    if (graphData.nodes.length > 0 && !hasZoomedRef.current && graphRef.current) {
      setTimeout(() => {
        graphRef.current?.zoomToFit(400, 60)
        hasZoomedRef.current = true
      }, 1000)
    }
  }, [graphData.nodes.length])

  const connectedIds = useMemo(() => {
    if (!hoveredNode) {
      return new Set<string>()
    }
    const ids = new Set<string>([hoveredNode.id])
    for (const link of graphData.links) {
      const src = typeof link.source === 'object' ? (link.source as GraphNode).id : link.source
      const tgt = typeof link.target === 'object' ? (link.target as GraphNode).id : link.target
      if (src === hoveredNode.id) ids.add(tgt)
      if (tgt === hoveredNode.id) ids.add(src)
    }
    return ids
  }, [hoveredNode, graphData.links])

  const childNodes = useMemo(() => {
    if (!selectedNode) return []
    return graphData.nodes.filter((n) => {
      const parentPath = n.id.substring(0, n.id.lastIndexOf('/'))
      return parentPath === selectedNode.id
    })
  }, [selectedNode, graphData.nodes])

  const handleNodeClick = useCallback((node: any) => {
    setSelectedNode(node as GraphNode)
    const g = graphRef.current
    if (!g) return
    const distance = 80
    const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z)
    g.cameraPosition(
      {
        x: node.x * distRatio,
        y: node.y * distRatio,
        z: node.z * distRatio,
      },
      node,
      600,
    )
  }, [])

  const handleNodeHover = useCallback((node: any) => {
    setHoveredNode(node as GraphNode | null)
  }, [])

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  const nodeThreeObject = useCallback(
    (node: any) => {
      const graphNode = node as GraphNode
      const isSelected = selectedNode?.id === graphNode.id
      const isHighlighted = hoveredNode && connectedIds.has(graphNode.id)

      const color = isSelected ? 0xf59e0b : NODE_COLORS[graphNode.color] || 0xa855f7

      const geometry = new THREE.SphereGeometry(graphNode.size * 1.5)
      const material = new THREE.MeshLambertMaterial({
        color,
        transparent: !!(hoveredNode && !isHighlighted && !isSelected),
        opacity: hoveredNode && !isHighlighted && !isSelected ? 0.2 : 1,
      })
      const mesh = new THREE.Mesh(geometry, material)

      // Add text sprite label
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      canvas.width = 256
      canvas.height = 64
      ctx.font = 'bold 28px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = hoveredNode && !isHighlighted ? 'rgba(100,100,100,0.3)' : '#374151'
      ctx.fillText(graphNode.label, 128, 32)

      const texture = new THREE.CanvasTexture(canvas)
      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
      })
      const sprite = new THREE.Sprite(spriteMaterial)
      sprite.scale.set(24, 6, 1)
      sprite.position.set(0, graphNode.size * 2 + 4, 0)

      const group = new THREE.Group()
      group.add(mesh)
      group.add(sprite)
      return group
    },
    [hoveredNode, connectedIds, selectedNode],
  )

  const linkColor = useCallback(
    (link: any) => {
      if (!hoveredNode) return 'rgba(100,116,139,0.6)'
      const graphLink = link as GraphLink
      const src =
        typeof graphLink.source === 'object' ? (graphLink.source as GraphNode).id : graphLink.source
      const tgt =
        typeof graphLink.target === 'object' ? (graphLink.target as GraphNode).id : graphLink.target
      if (connectedIds.has(src) && connectedIds.has(tgt)) {
        return 'rgba(245,158,11,1)'
      }
      return 'rgba(100,116,139,0.15)'
    },
    [hoveredNode, connectedIds],
  )

  const linkWidth = useCallback(
    (link: any) => {
      if (!hoveredNode) return 1
      const graphLink = link as GraphLink
      const src =
        typeof graphLink.source === 'object' ? (graphLink.source as GraphNode).id : graphLink.source
      const tgt =
        typeof graphLink.target === 'object' ? (graphLink.target as GraphNode).id : graphLink.target
      if (connectedIds.has(src) && connectedIds.has(tgt)) {
        return 2.5
      }
      return 0.5
    },
    [hoveredNode, connectedIds],
  )

  const graphWidth = selectedNode ? width - 320 : width

  return (
    <Box position="relative" w={width} h={height}>
      <ForceGraph3D
        ref={graphRef as any}
        graphData={graphData}
        width={graphWidth}
        height={height}
        nodeThreeObject={nodeThreeObject}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        onBackgroundClick={handleBackgroundClick}
        linkColor={linkColor}
        linkWidth={linkWidth}
        linkOpacity={0.8}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={1}
        linkDirectionalParticles={2}
        linkDirectionalParticleWidth={2}
        linkDirectionalParticleSpeed={0.005}
        d3AlphaDecay={0.1}
        d3VelocityDecay={0.5}
        // @ts-expect-error d3ReheatSimulation exists at runtime but is missing from type defs
        d3ReheatSimulation={!layoutFrozen}
        warmupTicks={100}
        cooldownTicks={200}
        onEngineStop={handleEngineStop}
        enableNodeDrag={true}
        enableNavigationControls={true}
        onNodeDragEnd={(node: any) => {
          node.fx = node.x
          node.fy = node.y
          node.fz = node.z
        }}
        backgroundColor="rgba(255,255,255,0)"
      />
      {selectedNode && (
        <KnowledgeGraphPanel
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          childNodes={childNodes}
        />
      )}
    </Box>
  )
}
